import type { ProductType, ProductTypeAssessment } from "@repo/shared/types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useDeleteProductTypeAssessment,
  useProductTypeAssessments,
  useUpsertProductTypeAssessment,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { type AssessmentModeDraft, useAssessmentModeDraft } from "./assessment-drafts";
import { T } from "./assessment-form-dialog.i18n";
import { AssessmentModeForm } from "./assessment-mode-form";

type AssessmentFormDialogProps = {
  open: boolean;
  collectionId: string;
  productType: ProductType | null;
  onClose: () => void;
};

export function AssessmentFormDialog({
  open,
  collectionId,
  productType,
  onClose,
}: AssessmentFormDialogProps) {
  const localize = useLocalize();
  const t = useT(T);

  const productTypeId = open ? (productType?.id ?? null) : null;
  const { assessments, isLoading } = useProductTypeAssessments(collectionId, productTypeId);

  const productTypeName = productType ? localize(productType.nameI18n) : "";

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t.title}
            {productTypeName ? ` — ${productTypeName}` : ""}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        {isLoading || !productType ? (
          <div className="space-y-2 py-4">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-9 w-40" />
          </div>
        ) : (
          // Keyed remount: each tab's draft state is initialised directly from the
          // loaded rows at mount. An effect-based sync would have to reconcile two
          // independent drafts against a refetch mid-edit, so remounting on a new
          // set of persisted ids stays the simpler, reliable path.
          <AssessmentTabs
            key={
              assessments
                .map((a) => a.id)
                .sort()
                .join("|") || "new"
            }
            assessments={assessments}
            collectionId={collectionId}
            productType={productType}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

type AssessmentTabsProps = {
  assessments: ProductTypeAssessment[];
  collectionId: string;
  productType: ProductType;
  onClose: () => void;
};

function AssessmentTabs({ assessments, collectionId, productType, onClose }: AssessmentTabsProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);

  const upsertAssessment = useUpsertProductTypeAssessment();
  const deleteAssessment = useDeleteProductTypeAssessment();
  const isPending = upsertAssessment.isPending || deleteAssessment.isPending;

  const existingQuiz = assessments.find((a) => a.mode === "QUIZ") ?? null;
  const existingQuestionnaire = assessments.find((a) => a.mode === "QUESTIONNAIRE") ?? null;

  // One draft per mode — called twice explicitly so a single Save can apply both.
  const quiz = useAssessmentModeDraft("QUIZ", existingQuiz, userLanguage);
  const questionnaire = useAssessmentModeDraft(
    "QUESTIONNAIRE",
    existingQuestionnaire,
    userLanguage,
  );
  // Quiz above questionnaire, matching the flow-run widget and the PDF breakdown.
  const drafts: AssessmentModeDraft[] = [quiz, questionnaire];

  const [activeTab, setActiveTab] = useState(
    existingQuestionnaire && !existingQuiz ? "QUESTIONNAIRE" : "QUIZ",
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const isValid = drafts.every((d) => d.isValid);
  // Turning a mode off deletes its questions, so that needs confirming first.
  const removesAMode = drafts.some((d) => !d.enabled && d.existing != null);

  const applyChanges = async () => {
    setError(null);
    try {
      // Sequential and independent per mode: an untouched mode issues no request.
      for (const draft of drafts) {
        if (draft.enabled) {
          if (draft.isDirty) {
            await upsertAssessment.mutateAsync({
              collectionId,
              ptId: productType.id,
              data: draft.toRequest(),
            });
          }
        } else if (draft.existing) {
          await deleteAssessment.mutateAsync({
            collectionId,
            ptId: productType.id,
            mode: draft.mode,
          });
        }
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isPending) return;
    if (removesAMode) {
      setConfirmRemoveOpen(true);
      return;
    }
    void applyChanges();
  };

  const tabLabel = (draft: AssessmentModeDraft, label: string) => (
    <span className="flex items-center gap-1.5">
      {label}
      {draft.enabled && !draft.isValid && (
        <span className="text-xs text-destructive">{t.incomplete}</span>
      )}
    </span>
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="QUIZ">{tabLabel(quiz, t.modeQuiz)}</TabsTrigger>
            <TabsTrigger value="QUESTIONNAIRE">
              {tabLabel(questionnaire, t.modeQuestionnaire)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="QUIZ">
            <AssessmentModeForm
              draft={quiz}
              supportedLanguages={tenantSupportedLanguages}
              disabled={isPending}
            />
          </TabsContent>
          <TabsContent value="QUESTIONNAIRE">
            <AssessmentModeForm
              draft={questionnaire}
              supportedLanguages={tenantSupportedLanguages}
              disabled={isPending}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          <Button type="submit" disabled={!isValid} loading={isPending}>
            {t.save}
          </Button>
        </DialogFooter>
      </form>

      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={t.removeTitle}
        description={t.removeDescription}
        confirmText={t.remove}
        cancelText={t.cancel}
        onConfirm={applyChanges}
        variant="destructive"
      />
    </>
  );
}
